import React, { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Modal } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  DropdownElement,
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './add-session-modal.scss';

// Store entities
import {
  createStoreSessionAttributes,
  updateStoreSessionAttributes
} from '../../store/session-settings/session-settings.thunks';
import { getSessionSettingsState } from '../../store/session-settings/session-settings.selectors';

// Models
import { TSessionType } from '../../types/settings.types';
import { IStoreSession } from '../../interfaces/session-settings.interfaces';

type TFormValue = {
  call_duration: string;
  // call_buffer: string;
};

type TAddSessionModalProps = {
  open: boolean;
  session_type: TSessionType | null;
  initialData?: IStoreSession | null;
  onSubmit?: (data: IStoreSession) => void;
  onCancel?: () => void;
}

export const AddSessionModal: FC<TAddSessionModalProps> = (
  {
    open = false,
    session_type,
    initialData = null,
    onSubmit = () => {},
    onCancel = () => {}
  }: TAddSessionModalProps
) => {
  const [form] = Form.useForm<TFormValue>();

  const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const { attributes, sessions } = useSelector(getSessionSettingsState);

  const dispatch = useDispatch<any>();

  const existVideoTimes = sessions.video_call_session.map(_ => +_.call_duration);
  const existAudioTimes = sessions.audio_call_session.map(_ => +_.call_duration);
  const existTextTimes = sessions.conversation_session.map(_ => +_.call_duration);

  const handleCancel = (_: MouseEvent<HTMLButtonElement>): void => {
    form.resetFields();
    setBtnDisabled(true);
    onCancel();
  };

  const durationsOptions: { value: string, label: string, disabled?: boolean }[] = useMemo(() => {
    if (!session_type) return [];
    if (session_type === 'audio_call') {
      return attributes.audio_call_duration?.map((duration) => ({
        value: `${ duration }`, label: `${ duration } minutes`, disabled: existAudioTimes.includes(duration)
      }));
    }
    if (session_type === 'conversation') {
      return attributes.conversation_duration?.map((duration) => ({
        value: `${ duration }`, label: `${ duration } minutes`, disabled: existTextTimes.includes(duration)
      }));
    }
    return attributes.video_call_duration?.map((duration) => ({
      value: `${ duration }`, label: `${ duration } minutes`, disabled: existVideoTimes.includes(duration)
    }));
  }, [session_type, existVideoTimes, existAudioTimes]);

  const bufferTimeOptions: { value: string, label: string }[] = useMemo(() => {
    if (!session_type) return [];
    if (session_type === 'audio_call') {
      return attributes.audio_call_buffer?.map((duration) => ({
        value: `${ duration }`, label: `${ duration } minutes`
      }));
    }
    if (session_type === 'conversation') {
      return attributes.conversation_buffer?.map((duration) => ({
        value: `${ duration }`, label: `${ duration } minutes`
      }));
    }
    return attributes.video_call_buffer?.map((duration) => ({
      value: `${ duration }`, label: `${ duration } minutes`
    }));
  }, [session_type]);

  const handleSubmit = async (): Promise<void> => {
    if (!session_type) return;

    setSavingChanges(true);
    const {
      call_duration,
      // call_buffer
    } = form.getFieldsValue();

    const { payload } = await dispatch(
      initialData ? updateStoreSessionAttributes({
        // call_buffer,
        call_duration,
        session_type,
        id: initialData.id
      }) : createStoreSessionAttributes({
        // call_buffer,
        call_duration,
        session_type
      })
    );

    setSavingChanges(false);

    if (!payload) return;
    onSubmit(payload);
    setBtnDisabled(true);
    form.resetFields();
  }

  const onValuesChange = (_: { [key in keyof TFormValue]: string }, {
    call_duration,
    // call_buffer
  }: { [key in keyof TFormValue]: string }): void => {
    setBtnDisabled(
      // (initialData?.call_buffer === call_buffer && initialData?.call_duration === call_duration) || !call_buffer || !call_duration
      (initialData?.call_duration === call_duration) || !call_duration
    );
  }

  useEffect(() => {
    if (!initialData) {
      form.resetFields();
      setBtnDisabled(true);
      return;
    }

    form.setFieldsValue({
      call_duration: `${ initialData.call_duration }`,
      // call_buffer: `${ initialData.call_buffer }`
    });
    setBtnDisabled(true);
  }, [initialData]);

  return (
    <Modal
      rootClassName="add-session-modal"
      width={ '395px' }
      open={ open }
      centered={ true }
      closeIcon={ false }
      title={ 'Add Session' }
      footer={ [
        <CustomButton key="cancel" text={ 'Cancel' } type={ 'text' } onClick={ handleCancel }/>,
        <CustomButton key="add" text={ initialData ? 'Update' : 'Add' } disabled={ btnDisabled }
                      loading={ savingChanges } onClick={ handleSubmit }/>
      ] }
      onCancel={ handleCancel }
    >
      <Form className="add-session-modal--body"
            form={ form }
            onValuesChange={ onValuesChange }
      >
        <div className="body-item flex-items">
          <Form.Item name="call_duration" initialValue={ null }>
            <DropdownElement options={ durationsOptions }
                             withoutAll={ true }
                             placeholder={ 'Duration*' }/>
          </Form.Item>
          {/*<Form.Item name="call_buffer" initialValue={ null }>*/}
          {/*  <DropdownElement options={ bufferTimeOptions }*/}
          {/*                   withoutAll={ true }*/}
          {/*                   placeholder={ 'Buffer Time*' }/>*/}
          {/*</Form.Item>*/}
        </div>
      </Form>
    </Modal>
  )
};
